import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import type { Article, ArticlesResponse } from '../types/blog'

interface UseArticlesParams {
  page?: number
  limit?: number
  category?: string
  threat_level?: string
  q?: string
}

interface UseArticlesResult {
  articles: Article[]
  total: number
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useArticles(params: UseArticlesParams = {}): UseArticlesResult {
  const [articles, setArticles] = useState<Article[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refetch = useCallback(() => setTick(t => t + 1), [])

  useEffect(() => {
    let cancelled = false

    async function fetchArticles() {
      setLoading(true)
      setError(null)

      try {
        const query = new URLSearchParams()
        if (params.page !== undefined) query.set('page', String(params.page))
        if (params.limit !== undefined) query.set('limit', String(params.limit))
        if (params.category) query.set('category', params.category)
        if (params.threat_level) query.set('threat_level', params.threat_level)
        if (params.q) query.set('q', params.q)

        const url = `/api/articles${query.toString() ? `?${query.toString()}` : ''}`
        const res = await axios.get<ArticlesResponse>(url)

        if (!cancelled) {
          setArticles(res.data.articles)
          setTotal(res.data.total)
        }
      } catch (err) {
        if (!cancelled) {
          setError('CONNECTION FAILED — Unable to retrieve intel feed')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchArticles()

    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.limit, params.category, params.threat_level, params.q, tick])

  return { articles, total, loading, error, refetch }
}
